import { Button, LinearProgress } from '@mui/joy';

import { useNavigate, useSearchParams } from 'react-router-dom';

import { useGroupedOrgInvites } from '@/components/NavSidebar/useGroupedInvites';
import { useLogout } from '@/hooks/useLogout';
import {
  useClaimPendingOrgInvite,
  useDeletePendingOrgInvite,
  useUser,
} from '@/hooks/useSwr';
import { appOrganizationPath } from '@/utils/constants';

import { NoMatch } from '../NoMatch';
import { isValidEmail, isValidUUID } from './utils';

export function AcceptInvite() {
  const [searchParams, _] = useSearchParams();
  const orgId = searchParams.get('org_id');
  let inviteEmail = searchParams.get('email');
  const deleteInvite = useDeletePendingOrgInvite();
  const claimInvite = useClaimPendingOrgInvite();
  const orgInvites = useGroupedOrgInvites();
  const curInvite = orgInvites.data?.find((invite) => invite.id === orgId);

  const navigate = useNavigate();
  const logout = useLogout();
  const user = useUser();
  const email = user?.data?.data.user?.email;

  inviteEmail = decodeURI(inviteEmail ?? '');

  if (orgInvites.isLoading) {
    return (
      <div className="flex min-h-screen flex-col">
        <div>
          <LinearProgress />
        </div>
      </div>
    );
  }

  if (
    !orgId ||
    !inviteEmail ||
    !isValidUUID(orgId) ||
    !isValidEmail(inviteEmail)
  ) {
    return (
      <div className="p-4">
        <NoMatch />
      </div>
    );
  }

  if (!curInvite) {
    return (
      <div className="min-w-screen flex min-h-screen flex-col items-center justify-center">
        <div className="flex max-w-[600px] flex-col gap-4 rounded-lg bg-secondary p-4">
          <div className="text-lg font-semibold">No pending invites</div>
          <div className="text-md">
            The invite to join organization with ID <strong>{orgId}</strong> was
            sent to <strong>{inviteEmail}</strong>, but you are logged in as{' '}
            <strong>{email}</strong>.
          </div>
          <div className="text-md">
            To accept the invite, please log out and sign in with the correct
            email address. If this seems like a mistake, please contact your
            organization admin for assistance.
          </div>
          <div className="flex justify-end gap-2">
            <Button onClick={() => logout()}>Logout</Button>
            <Button
              color="neutral"
              variant="outlined"
              onClick={() => navigate('/')}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-w-screen flex min-h-screen flex-col items-center justify-center">
      <div className="flex flex-col gap-4 rounded-lg bg-secondary p-4">
        <div className="text-lg font-semibold">
          Accept pending organization invite
        </div>
        <div className="text-md">
          You were invited to workspaces{' '}
          <strong>
            {curInvite.workspaceInvites?.map((w) => w.display_name).join(', ')}
          </strong>{' '}
          in the <strong>{curInvite.display_name}</strong> organization
        </div>

        <div className="flex justify-end gap-2">
          <Button
            color="neutral"
            variant="outlined"
            onClick={() => deleteInvite.trigger(orgId)}
          >
            Decline
          </Button>
          <Button
            color="primary"
            onClick={async () => {
              await claimInvite.trigger(orgId);
              if (curInvite.workspaceInvites?.[0]) {
                navigate(
                  `/${appOrganizationPath}/${curInvite.workspaceInvites[0].id}`
                );
              }
            }}
            loading={claimInvite.isMutating}
          >
            Accept
          </Button>
        </div>
      </div>
    </div>
  );
}
